/**
 * SOLID Review
 * S — Strong. sole job: implement IAdapterApolloSession's session/identity operations against the real
 *     backend via Apollo's imperative client API. Strong for the one method it has (createPlayer
 *     — Phase 2 Group D, plan/plan_frontend_makeover.md, IdentityModal.tsx's real guest-identity
 *     operation). initializeSession/getLocalUser/getAdConfig/getLeaderboard were deleted as
 *     housekeeping (plan/plan_frontend_makeover.md) — fabricated/idealized placeholders with
 *     zero known callers anywhere in the tree, confirmed after Phase 2 completed.
 * O — Good. A new session operation is a new method here plus a new IAdapterApolloSession signature —
 *     mechanical, no existing method touched.
 * L — N/A — no inheritance hierarchy; implements IAdapterApolloSession directly.
 * I — Good. Implements the same interface every real session operation needs — no longer silently narrower
 *     than the interface promises now that its four fabricated placeholders
 *     (initializeSession/getLocalUser/getAdConfig/getLeaderboard) are gone.
 * D — Strong. Takes its domain's GraphQL port and the logger as constructor-injected dependencies (same
 *     pattern as ApolloRoomAdapter.ts/ApolloGameAdapter.ts) rather than importing a singleton — testable
 *     without a real network. Its calls go through `GraphQLCall`, which answers the port's failure (§28.288).
 */
import { gql } from '@apollo/client';
import type { Result } from '../../shared/Result';
import type { IEntityCreatePlayer } from '../../application/ports/apolloShapes';
import type { IAdapterApolloSession } from '../../application/ports/IAdapterApolloSession';
import type { IPortGraphQLDomainClient } from '../contract/ports';
import type { IServiceLogger } from '../platform/IServiceLogger';
import { GraphQLCall } from './GraphQLCall';

// ---------------------------------------------------------------------------
// GraphQL operations (Phase 2 Group D). Moved here from IdentityModal.tsx's inline definition.
// ---------------------------------------------------------------------------

const CREATE_PLAYER_MUTATION = gql`
  mutation ApolloSessionAdapter_CreatePlayer($displayName: String!) {
    createPlayer(displayName: $displayName) {
      id
      displayName
      matchCredits
      lives
    }
  }
`;

export class ApolloSessionAdapter implements IAdapterApolloSession {
  private readonly call: GraphQLCall;

  // The executor arrives from this domain's socket on ComponentFrontendInfrastructure, not as
  // the raw ApolloClient. That socket is the single path from "this app can reach the domain"
  // to "this adapter executes" — disconnect it and this adapter degrades to the Null client
  // instead of quietly using a module singleton (§28.72).
  constructor(client: IPortGraphQLDomainClient, logger: IServiceLogger) {
    this.call = new GraphQLCall('ApolloSessionAdapter', client, logger);
  }

  async createPlayer(displayName: string): Promise<Result<IEntityCreatePlayer>> {
    return this.call.required('createPlayer', 'creating player', (apollo) => apollo.mutate<{ createPlayer: IEntityCreatePlayer | null }>({
      mutation: CREATE_PLAYER_MUTATION,
      variables: { displayName },
    }), (data) => data.createPlayer);
  }
}
